import { useMemo } from 'react';
import { userApi } from '@vmos2/core';
import { useConfig, useSettings } from '@vmos2/providers';
import { useHybridApp, arrayBufferToBase64, useRequest, useLocale } from '@vmos2/utils';
import { useTranslation } from 'react-i18next';
import { AxiosResponse } from 'axios';

const PK_PASS_CONTENT_TYPE = 'application/vnd.apple.pkpass';

function isIOSOnSafari() {
  if (!navigator?.userAgent) return false;

  return (
    navigator.userAgent.includes('Safari') &&
    navigator.userAgent.includes('AppleWebKit') &&
    !navigator.userAgent.includes('Chrome') &&
    /iPad|iPhone|iPod/.test(navigator.userAgent)
  );
}

export const useAppleWalletPass = () => {
  const config = useConfig();
  const settings = useSettings();
  const { locale } = useLocale(config, settings);
  const { S3_URL, bootstrap } = useConfig();
  const { hybridApp, isLoading: isLoadingHybridApp } = useHybridApp();
  const { t } = useTranslation('profile.rewards');
  const walletPassFileName = t('appleWalletPass.fileName');
  const brandUUID = bootstrap?.hostBrands?.[0]?.uuid;

  const [{ isLoading, error }, getWalletPass] = useRequest(
    async () => {
      const response: AxiosResponse = await userApi.get('v2/wallet-pass/apple', {
        params: { locale, brandUUID },
        responseType: 'arraybuffer',
        raw: true,
      });

      if (!response?.data) {
        throw new Error('Failed to retrieve wallet pass');
      }

      let url = null;
      const isFile = response.headers?.['content-type'].includes(PK_PASS_CONTENT_TYPE);

      if (isFile) {
        if (hybridApp?.addIosWalletPass) {
          const base64Pass = arrayBufferToBase64(response.data);

          await hybridApp.addIosWalletPass(base64Pass);
          return;
        }

        url = window.URL.createObjectURL(new Blob([response.data], { type: PK_PASS_CONTENT_TYPE }));

        if (!url) {
          throw new Error('Failed to generate wallet pass data link');
        }
      } else {
        const decoder = new TextDecoder('utf-8');
        const decodedArrayBuffer = decoder.decode(response.data);
        const data = JSON.parse(decodedArrayBuffer);
        url = data?.payload;

        if (!url) {
          throw new Error('Failed to generate wallet pass data link');
        }

        if (hybridApp?.openSystemLink) {
          hybridApp.openSystemLink(url);
          return;
        }
      }

      const link = document.createElement('a');
      link.href = url;

      if (isFile) {
        link.setAttribute('download', `${walletPassFileName}.pkpass`);
      } else if (!isIOSOnSafari()) {
        link.rel = 'noopener noreferrer';
        link.target = '_blank';
      }

      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  );

  const buttonImageUrl = useMemo(() => {
    const languageCode = locale.substring(0, locale.indexOf('-')).toUpperCase();

    return `${S3_URL}/static/add-to-apple-wallet/add-to-apple-wallet-${languageCode}.svg`;
  }, [S3_URL, locale]);

  const isApplicableDevice = useMemo(() => {
    if (isLoadingHybridApp) {
      return false;
    }

    if (hybridApp) {
      const { isReactNativeApp, platformId } = hybridApp;
      const isIosOS = !!(isReactNativeApp && (platformId === 'ios' || platformId === 'ipados'));

      return isIosOS;
    }

    const userAgent = navigator?.userAgent || navigator?.vendor || (window as any)?.opera || '';
    const appleDeviceRegex = /iPhone|iPad|Macintosh/i;

    return appleDeviceRegex.test(userAgent);
  }, [isLoadingHybridApp, hybridApp]);

  return {
    isLoading,
    hasError: !!error,
    isApplicableDevice,
    buttonImageUrl,
    getWalletPass,
  };
};
